import { bool, func, node, number, oneOfType, string } from "prop-types";
import { forwardRef, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";
import { themeSizes } from "theme";

import FnrTypography from "FnrTypography";

import FnrButton from "UI/FnrButton3/FnrButton";
import FnrInput2 from "UI/FnrInput2/FnrInput2";

const Container = styled("div")`
  display: flex;
  align-items: ${({ $multiline }) => ($multiline ? "end" : "center")};
`;

const buttonFauxPadding = ({ $size, $stout }) => {
  switch ($size) {
    case themeSizes.xs:
      return 4;
    case themeSizes.sm:
      return 6;
    case themeSizes.lg:
      return $stout ? 7 : 8;
    case themeSizes.xl:
      return 14;
    case themeSizes.md: // also default
    default:
      return 8;
  }
};

const buttonHeight = ({ $size }) => {
  switch ($size) {
    case themeSizes.xs:
      return "height: 28px";
    case themeSizes.sm:
      return "height: 32px";
    case themeSizes.lg:
    case themeSizes.xl:
      return "height: 44px";
    case themeSizes.md:
    default:
      return "height: 36px";
  }
};

const ButtonContainer = styled("div")`
  width: 0px;
  z-index: 11; // one index higher than than FnrInput2's label.
  & button {
    transform: translateX(-100%)
      translateX(
        -${({ $size, $stout }) => buttonFauxPadding({ $size, $stout }) || 8}px
      )
      translateY(
        -${({ $size, $stout, $multiline }) => ($multiline ? (buttonFauxPadding({ $size, $stout }) || 8) + 4 : 0)}px
      );
    ${buttonHeight}
  }
`;

const InputContainer = styled("div")`
  flex-grow: 1;
  & input {
    padding-right: ${({ $buttonWidth, $size, $stout }) =>
      $buttonWidth + (buttonFauxPadding({ $size, $stout }) || 8) * 2}px;
  }
  & textarea {
    padding-bottom: 56px;
  }
`;

const FnrTypeNSave = forwardRef(
  (
    {
      onSubmit,
      buttonText = "Save",
      isLoading,
      autofocus,
      setValue,
      value = "",
      disabled,
      buttonColor = "green",
      buttonTextColor = "textLight",
      isLegacy = true,
      size,
      stout,
      children, // use only with FauxInput helper component (./FauxInput)
      charLimit,
      buttonAriaLabel,
      ...rest
    },
    ref
  ) => {
    const localRef = useRef();
    const inputRef = ref || localRef;
    const buttonRef = useRef();
    const [inputValue, setInputValue] = useState(value);

    const [buttonWidth, setButtonWidth] = useState(0);

    useEffect(() => {
      setButtonWidth(buttonRef.current?.clientWidth || 0);
    }, [buttonText]);

    const handleSetInputValue = (newValue) => {
      if (setValue) setValue(newValue);
      if (!charLimit) setInputValue(newValue);
      else if (newValue.length <= charLimit) setInputValue(newValue);
    };
    const hasValue = inputValue !== "" || value !== "";

    const handleSubmit = () => {
      if (hasValue && !disabled) {
        onSubmit(inputValue);
      }
    };

    useEffect(
      function forceAutoFocus() {
        /**
         * The input was losing focus from the autofocus property before fade in animations finished
         * This is a hacky way to force the input to stay in focus through the animation
         */
        if (!autofocus || typeof autofocus === "number") return;

        let current = inputRef?.current;

        const interval = setInterval(() => {
          current.focus();
        }, 100);

        setTimeout(() => {
          clearInterval(interval);
        }, 300);
      },
      [autofocus, inputRef]
    );

    return (
      <form onSubmit={handleSubmit}>
        <Container $multiline={rest.multiline}>
          <InputContainer $buttonWidth={buttonWidth}>
            <FnrInput2
              ref={inputRef}
              value={inputValue || value}
              setValue={handleSetInputValue}
              disabled={isLoading}
              autofocus={autofocus}
              size={size}
              stout={stout}
              {...rest}
            />
          </InputContainer>
          <ButtonContainer
            $size={size}
            $stout={stout}
            $multiline={rest.multiline}
          >
            <FnrButton
              disabled={!hasValue || isLoading || disabled}
              color={hasValue && !disabled ? buttonColor : "gray"}
              onClick={handleSubmit}
              loading={isLoading}
              ref={buttonRef}
              preventDefault
              aria-label={buttonAriaLabel || "save"}
            >
              <FnrTypography
                type={isLegacy ? undefined : "accent"}
                bold={false}
                color={buttonTextColor}
              >
                {buttonText}
              </FnrTypography>
            </FnrButton>
          </ButtonContainer>
          {children}
        </Container>
      </form>
    );
  }
);

FnrTypeNSave.propTypes = {
  /** Returns the types string */
  onSubmit: func.isRequired,
  /** Use to customize the button text */
  buttonText: string,
  /** disables the input and sets button to loading */
  isLoading: bool,
  autofocus: oneOfType([bool, number]),
  setValue: func,
  value: string,
  disabled: bool,
  buttonColor: string,
  buttonTextColor: string,
  isLegacy: bool,
  size: string,
  stout: bool,
  children: node,
  charLimit: number,
  buttonAriaLabel: string,
};
FnrTypeNSave.name = "FnrTypeNSave";

export default FnrTypeNSave;
